import React from 'react';
import { motion } from 'framer-motion';
import { Factory, Building2, Droplets, Zap, ArrowRight, CheckCircle, Mountain, Fish, Leaf, Waves } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const IndustriesOverviewPage: React.FC = () => {
  const industries = [
    {
      id: 'agriculture',
      title: 'Agriculture',
      icon: Droplets,
      description: 'Protecting irrigation networks, orchards, and vineyards from debris and scale. Our filtration systems extend emitter life, stabilize pressure, and simplify maintenance.',
      applications: [
        'Orchard and vineyard irrigation',
        'Drip and micro systems',
        'Pivot and solid-set',
        'Reclaimed water reuse'
      ],
      color: 'green',
      link: '/industries/agriculture'
    },
    {
      id: 'municipal',
      title: 'Municipal Water & Wastewater',
      icon: Building2,
      description: 'From surface-water intakes to pump-station pre-filters, our stainless-steel systems provide reliable solids removal for municipalities large and small.',
      applications: [
        'Raw-water intake protection',
        'Pre-treatment before sand or membrane filtration',
        'Lift-station and pump-station screening',
        'Reclaimed-water discharge'
      ],
      color: 'blue',
      link: '/industries/municipal'
    },
    {
      id: 'industrial',
      title: 'Industrial & Process Water',
      icon: Factory,
      description: 'CTC filtration systems serve food-and-beverage, packing, manufacturing, and washdown operations requiring reliable solids separation.',
      applications: [
        'Packing-line wash water',
        'Spray-bar protection',
        'Coolant filtration',
        'Rinse-water recovery',
        'Industrial wastewater polishing'
      ],
      color: 'orange',
      link: '/industries/industrial'
    },
    {
      id: 'hydropower',
      title: 'Hydropower & Dams',
      icon: Zap,
      description: 'Designed for the most demanding surface-water intakes, CTC self-cleaning suction screens protect turbines, penstocks, and downstream systems.',
      applications: [
        'Hydropower intakes',
        'Dam penstock screening',
        'Fish-passage systems',
        'Canal and river diversions'
      ],
      color: 'purple',
      link: '/industries/hydropower'
    },
    {
      id: 'mining',
      title: 'Mining & Quarrying',
      icon: Mountain,
      description: 'Stainless-steel filters and suction screens built to handle abrasive flows in mining, aggregate washing, and dewatering systems.',
      applications: [
        'Mine dewatering',
        'Process-water loops',
        'Aggregate washing',
        'Tailings pond recirculation'
      ],
      color: 'gray',
      link: '/industries/mining'
    },
    {
      id: 'aquaculture',
      title: 'Aquaculture & Fisheries',
      icon: Fish,
      description: 'Self-cleaning intake screens and inline filters for hatcheries, recirculating aquaculture systems, and pond filtration — meeting NOAA/WDFW fish-screen standards.',
      applications: [
        'Hatchery intake protection',
        'Tank recirculation',
        'RAS polishing',
        'Pond and canal filtration'
      ],
      color: 'cyan',
      link: '/industries/aquaculture'
    },
    {
      id: 'environmental',
      title: 'Environmental Surface Water',
      icon: Leaf,
      description: 'Filtration for watershed projects, stormwater reuse, and habitat restoration — designed for compliance with fish-screen and debris-exclusion criteria.',
      applications: [
        'Surface-water intakes',
        'Habitat restoration',
        'Stormwater treatment',
        'Environmental sampling'
      ],
      color: 'emerald',
      link: '/industries/environmental'
    },
    {
      id: 'desalination',
      title: 'Desalination & Water Reuse',
      icon: Waves,
      description: 'Reliable stainless-steel pre-filtration for seawater intake and membrane protection in desalination and water-reuse applications.',
      applications: [
        'RO/UF pretreatment',
        'Seawater intake',
        'Brackish water reuse',
        'Municipal reclamation facilities'
      ],
      color: 'teal',
      link: '/industries/desalination'
    }
  ];

  const colorClasses = {
    green: {
      bg: 'from-green-50 to-white',
      border: 'border-green-300',
      icon: 'bg-green-600',
      button: 'border-green-600 text-green-600 hover:bg-green-600'
    },
    blue: {
      bg: 'from-blue-50 to-white',
      border: 'border-blue-300',
      icon: 'bg-blue-600',
      button: 'border-blue-600 text-blue-600 hover:bg-blue-600'
    },
    orange: {
      bg: 'from-orange-50 to-white',
      border: 'border-orange-300',
      icon: 'bg-orange-600',
      button: 'border-orange-600 text-orange-600 hover:bg-orange-600'
    },
    purple: {
      bg: 'from-purple-50 to-white',
      border: 'border-purple-300',
      icon: 'bg-purple-600',
      button: 'border-purple-600 text-purple-600 hover:bg-purple-600'
    },
    gray: {
      bg: 'from-gray-50 to-white',
      border: 'border-gray-300',
      icon: 'bg-gray-700',
      button: 'border-gray-700 text-gray-700 hover:bg-gray-700'
    },
    cyan: {
      bg: 'from-cyan-50 to-white',
      border: 'border-cyan-300',
      icon: 'bg-cyan-600',
      button: 'border-cyan-600 text-cyan-600 hover:bg-cyan-600'
    },
    emerald: {
      bg: 'from-emerald-50 to-white',
      border: 'border-emerald-300',
      icon: 'bg-emerald-600',
      button: 'border-emerald-600 text-emerald-600 hover:bg-emerald-600'
    },
    teal: {
      bg: 'from-teal-50 to-white',
      border: 'border-teal-300',
      icon: 'bg-teal-600',
      button: 'border-teal-600 text-teal-600 hover:bg-teal-600'
    }
  };

  return (
    <>
      <SEOHead
        title="Industries Served | CTC Waterworks"
        description="Explore CTC Waterworks filtration solutions for agriculture, municipal, industrial, hydropower, mining, aquaculture, environmental, and desalination applications."
        canonicalPath="/industries"
      />
      <div className="min-h-screen bg-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-ctc-blue to-blue-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-5xl font-bold mb-6">
                Industries Served
              </h1>
              <p className="text-2xl text-blue-100 mb-8 font-light leading-relaxed max-w-4xl mx-auto">
                From raw-water intakes to process filtration, CTC systems are built to perform — no matter the industry.
              </p>
              <p className="text-lg text-blue-100 leading-relaxed max-w-3xl mx-auto">
                Our stainless-steel filters and suction screens are trusted by growers, engineers, and municipalities across North America. Explore how our systems adapt to your environment.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Industries Grid */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {industries.map((industry, index) => {
                const Icon = industry.icon;
                const colors = colorClasses[industry.color as keyof typeof colorClasses];

                return (
                  <motion.div
                    key={industry.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className={`bg-gradient-to-br ${colors.bg} border-2 ${colors.border} rounded-lg p-8 hover:shadow-xl transition-shadow`}
                  >
                    <div className="flex items-center mb-6">
                      <div className={`w-16 h-16 ${colors.icon} rounded-lg flex items-center justify-center mr-4`}>
                        <Icon className="text-white" size={32} />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">{industry.title}</h2>
                      </div>
                    </div>

                    <p className="text-gray-700 mb-6 leading-relaxed">
                      {industry.description}
                    </p>

                    <div className="mb-6">
                      <h3 className="font-semibold text-gray-900 mb-3">Common Applications:</h3>
                      <ul className="space-y-2">
                        {industry.applications.map((app, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className={`mr-2 mt-1 flex-shrink-0 text-${industry.color}-600`} size={18} />
                            <span className="text-sm text-gray-700">{app}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link
                      to={industry.link}
                      className={`flex items-center justify-between bg-white border-2 ${colors.button} hover:text-white px-4 py-3 rounded-lg font-semibold transition-colors`}
                    >
                      <span>Learn More</span>
                      <ArrowRight size={20} />
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-ctc-blue to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Let's Engineer Your Solution</h2>
            <p className="text-xl text-blue-100 mb-8">
              From raw-water intakes to process filtration, our systems are built to perform — no matter the industry.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact?topic=filtration"
                className="inline-flex items-center justify-center bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                <span>Talk to a Filtration Expert</span>
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <a
                href="tel:+15099017476"
                className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-ctc-blue transition-colors"
              >
                <span>(509) 901-7476</span>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default IndustriesOverviewPage;
